import React, { useState } from 'react';
import CCEvents, { EventNames } from '@companycam/companycam-events';
import Steven from 'helpers/steven';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { useIntl, defineMessages } from 'react-intl';
import styled from 'styled-components';
import { tokens } from '@companycam/slab';
import { graphQLIdDecoder } from 'helpers/graphql-utils';
import Common from 'helpers/intl-common';
import useShowHasTaskListTemplateTooltip from 'components/projects/taskList/hooks/useShowTaskListTemplateTooltip';
import { getGqlErrors } from 'components/projects/taskList/helpers';
import TASK_LIST_CREATE from 'components/projects/taskList/gql/taskListCreate.graphql';
import TASK_LIST_UPDATE from 'components/projects/taskList/gql/taskListUpdate.graphql';
import TASK_LIST_TEMPLATE_CREATE_FROM_TASK_LIST from 'components/projects/taskList/gql/taskListTemplateCreateFromTaskList.graphql';
import toast from 'helpers/toast';

import Modal from 'components/shared/DesignSystem/Modal';
import TaskListError from 'components/projects/taskList/components/TaskListError';
import { Button } from '@companycam/slab-web';

// #region styled-components
const StyledModal = styled(Modal)``;

const TaskListLabel = styled.label``;

const TaskListTitleInput = styled.input`
  margin-bottom: 24px;
`;
const TaskListTemplateDescriptionInput = styled.input`
  margin-bottom: 24px;
`;

const Container = styled.div``;

const NOOP = () => {};

const messages = defineMessages({
  addTaskListModalTitle: {
    id: 'Projects.taskList.addTaskListModalTitle',
    defaultMessage: 'New Checklist',
    description: 'Title for task list form modal',
  },
  editTaskListModalTitle: {
    id: 'Projects.taskList.editTitle',
    defaultMessage: 'Edit Checklist',
    description: 'Title for task list form modal: edit task list',
  },
  taskListFormLabel: {
    id: 'Projects.taskList.taskListFormLabel',
    defaultMessage: 'Checklist Title',
    description: 'Label for task list form modal',
  },
  taskListNameInputPlaceholder: {
    id: 'Projects.taskList.taskListNameInputPlaceholder',
    defaultMessage: 'Untitled Checklist',
    description: 'Placeholder text for task list title input',
  },
  taskListCategoriesInstructions: {
    id: 'Projects.taskList.taskListCategoriesInstructions',
    defaultMessage:
      "Tell us a little more about how you'll use this checklist (optional)",
    description: 'Placeholder text for task list categories instructions text',
  },
  addTaskListButtonText: {
    id: 'Projects.taskList.addTaskListButtonText',
    defaultMessage: 'Create Checklist',
    description: 'Button text for adding task list',
  },
  editTaskListButtonText: {
    id: 'Common.save',
    defaultMessage: 'Save',
    description: 'Button text for editing task list',
  },
  addNewTaskListError: {
    id: 'Projects.taskList.addNewTaskListError',
    defaultMessage: 'There was an error adding this checklist',
    description: 'Error message when adding a task list',
  },
  editTaskListError: {
    id: 'Projects.taskList.editTaskListError',
    defaultMessage: 'There was an error editing this checklist',
    description: 'Error message editing a task list',
  },
  templateCreated: {
    id: 'Projects.taskList.TaskListFormModal.templateCreated',
    defaultMessage: 'Template Created',
    description: 'message displayed after a template is created',
  },
  templateCreatedError: {
    id: 'Projects.taskList.TaskListFormModal.templateCreatedError',
    defaultMessage: 'There was an error creating the template',
    description: 'message displayed when an error occurs creating a template',
  },
  templateCreate: {
    id: 'Projects.taskList.TaskListFormModal.templateCreate',
    defaultMessage: 'Create New Template',
    description: 'text for creating a new templated',
  },
  templateCreateFromToDo: {
    id: 'Projects.taskList.TaskListFormModal.templateCreateFromToDo',
    defaultMessage: 'Save List as Template',
    description: 'drop down option for saving checklist as a template',
  },
  templateDescriptionPlaceholder: {
    id: 'Projects.taskList.TaskListFormModal.templateDescriptionPlaceholder',
    defaultMessage: 'Add a template description',
    description: 'input placeholder text for template description text',
  },
  templateDescriptionLabel: {
    id: 'Projects.taskList.TaskListFormModal.templateDescriptionLabel',
    defaultMessage: 'Template Description',
    description: 'Label for template description input field',
  },
  templateLoadingLabel: {
    id: 'Projects.taskList.TaskListFormModal.buttonLoadingLabel',
    defaultMessage: 'Creating Template...',
    description: 'Button label while create template loading',
  },
});
// #endregion

const getTaskListIdFromCreate = (data) =>
  get(data, 'taskListCreate.taskList.id', '');

const TaskListFormModal = ({
  projectNodeId,
  taskList = null,
  isOpen = false,
  onRequestClose = NOOP,
  projectId,
  templateCreateFromTaskList,
}) => {
  const intl = useIntl();
  const [taskListName, setTaskListName] = useState(
    taskList ? taskList.name : '',
  );
  const [taskListTemplateDesciption, setTaskListTemplateDescription] =
    useState('');
  const [taskListFormError, setTaskListFormError] = useState({ msg: '' });

  const handleTextInputChange = (text) => {
    if (taskListFormError.msg.length) {
      setTaskListFormError({ msg: '' });
    }
    setTaskListName(text);
  };

  const resetTaskListForm = () => {
    setTaskListFormError({ msg: '' });
    setTaskListName(taskList ? taskList.name : '');
    setTaskListTemplateDescription('');
  };

  const closeResetModalForm = () => {
    resetTaskListForm();
    onRequestClose();
  };

  const { onTaskListCreate } = useShowHasTaskListTemplateTooltip();

  const [createTaskListMutation] = useMutation(TASK_LIST_CREATE, {
    onCompleted: (data) => {
      const gqlErrors = getGqlErrors(data);
      const taskListId = getTaskListIdFromCreate(data);
      const dbId = graphQLIdDecoder(taskListId)?.databaseId;
      if (!gqlErrors.length && dbId) {
        closeResetModalForm();
        Steven.entityCrud({
          action: 'created',
          entity_id: dbId,
          entity_type: 'checklist',
        });
        window.Turbo.visit(`/projects/${projectId}/todos/${taskListId}`);
        onTaskListCreate();
      }
    },
    onError: () => {
      setTaskListFormError({
        msg: intl.formatMessage(messages.addNewTaskListError),
      });
    },
  });

  const [editTaskListMutation] = useMutation(TASK_LIST_UPDATE, {
    onCompleted: (data) => {
      const gqlErrors = getGqlErrors(data);
      if (gqlErrors.length) {
        setTaskListFormError({
          msg: gqlErrors[0].message,
        });
        return;
      }
      closeResetModalForm();
    },
    onError: () => {
      setTaskListFormError({
        msg: intl.formatMessage(messages.editTaskListError),
      });
    },
  });

  const [createTemplateFromTaskListMutation, { loading }] = useMutation(
    TASK_LIST_TEMPLATE_CREATE_FROM_TASK_LIST,
    {
      update: (_, { data }) => {
        const gqlErrors = getGqlErrors(data);

        if (!gqlErrors.length) {
          toast.success(intl.formatMessage(messages.templateCreated));
        } else {
          toast.error(gqlErrors[0].message);
        }
      },
      onCompleted: (data) => {
        const gqlErrors = getGqlErrors(data);

        if (!gqlErrors.length) {
          closeResetModalForm();
          CCEvents.logEvent(EventNames.TASK_LIST_TEMPLATES.CREATE_TEMPLATE);
        }
      },
      onError: () => {
        toast.error(intl.formatMessage(messages.templateCreatedError));
      },
    },
  );

  const createTaskList = async () => {
    await createTaskListMutation({
      variables: {
        input: {
          name: taskListName.trim(),
          projectId: projectNodeId,
          category: null,
        },
      },
    });
  };

  const editTaskList = () => {
    if (taskListName !== taskList.name) {
      editTaskListMutation({
        variables: {
          input: {
            name: taskListName.trim(),
            id: taskList.id,
          },
        },
      });
    }
  };

  const createTemplateFromTaskList = () => {
    createTemplateFromTaskListMutation({
      variables: {
        input: {
          taskListTemplate: {
            name: taskListName,
            description: taskListTemplateDesciption,
            id: taskList.id,
          },
        },
      },
    });
  };

  const renderButtonText = () => {
    if (loading) {
      return intl.formatMessage(messages.templateLoadingLabel);
    }
    if (templateCreateFromTaskList)
      return intl.formatMessage(messages.templateCreate);

    if (taskList) return intl.formatMessage(messages.editTaskListButtonText);

    return intl.formatMessage(messages.addTaskListButtonText);
  };

  const renderTitleText = () => {
    if (templateCreateFromTaskList)
      return intl.formatMessage(messages.templateCreateFromToDo);

    if (taskList) return intl.formatMessage(messages.editTaskListModalTitle);

    return intl.formatMessage(messages.addTaskListModalTitle);
  };

  const handleTaskListCreate = () => {
    if (templateCreateFromTaskList) return createTemplateFromTaskList();

    if (taskList) return editTaskList();

    return createTaskList().then(() => {
      CCEvents.logEvent(EventNames.TASK_LISTS.CREATE_LIST);
    });
  };

  return (
    <StyledModal
      isOpen={isOpen}
      isClosable
      onRequestClose={closeResetModalForm}
      title={renderTitleText()}
      className="modal-content-wrap"
      isTemplate={templateCreateFromTaskList}
      autoFocus={false}
    >
      <Container>
        <TaskListLabel htmlFor="taskListName">
          {intl.formatMessage(Common.title)}
        </TaskListLabel>
        <TaskListTitleInput
          autoFocus
          id="taskListName"
          onChange={(e) => handleTextInputChange(e.target.value)}
          value={taskListName}
          placeholder={intl.formatMessage(
            messages.taskListNameInputPlaceholder,
          )}
          placeholderTextColor={tokens.color_text_nonessential_le}
        />
        {templateCreateFromTaskList ? (
          <>
            <TaskListLabel htmlFor="taskListName">
              {intl.formatMessage(messages.templateDescriptionLabel)}
            </TaskListLabel>
            <TaskListTemplateDescriptionInput
              id="taskListName"
              onChange={(e) => setTaskListTemplateDescription(e.target.value)}
              value={taskListTemplateDesciption}
              placeholder={intl.formatMessage(
                messages.templateDescriptionPlaceholder,
              )}
              placeholderTextColor={tokens.color_text_nonessential_le}
            />
          </>
        ) : null}
        {taskListFormError.msg.length ? (
          <TaskListError message={taskListFormError.msg} />
        ) : null}
        <div className="btn-container">
          <Button
            color="primary"
            onClick={handleTaskListCreate}
            data-testid="project__checklists__modal__create-checklist-button"
            loading={loading}
            disabled={!taskListName.length}
          >
            {renderButtonText()}
          </Button>
        </div>
      </Container>
    </StyledModal>
  );
};

TaskListFormModal.propTypes = {
  projectNodeId: PropTypes.string,
  projectId: PropTypes.number,
  isOpen: PropTypes.bool,
  onRequestClose: PropTypes.func,
  taskList: PropTypes.object,
  templateCreateFromTaskList: PropTypes.bool,
};

export default TaskListFormModal;
